import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { CatalogCard } from '../components/CatalogCard';
import { useAuth } from '../context/AuthContext';
import { 
  Bookmark, 
  Loader2, 
  ArrowLeft,
  Heart,
  ShoppingBag,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { formatDistanceToNow } from 'date-fns';
import { useTileSize } from '../hooks/useTileSize';
import { TileSizeSlider } from '../components/TileSizeSlider';

export default function SavedItemsPage() {
  const { user, api } = useAuth();
  const { tileSize, setTileSize, gridStyle, MIN_TILE_SIZE, MAX_TILE_SIZE } = useTileSize();
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSavedItems();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchSavedItems = async () => {
    try {
      const res = await api.get('/inventory/saved');
      setSavedItems(res.data.items || []);
    } catch (error) {
      console.error('Failed to fetch saved items:', error);
      toast.error('Failed to load saved items');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (itemId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await api.delete(`/inventory/${itemId}/save`);
      setSavedItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Item removed from saved');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen bg-[var(--theme-bg)] flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            <Bookmark className="w-16 h-16 mx-auto text-[var(--theme-text-muted)] mb-4" />
            <h2 className="text-xl font-semibold text-[var(--theme-text-primary)] mb-2">Sign in to view saved items</h2>
            <p className="text-[var(--theme-text-secondary)] mb-6">
              Save listings you're interested in and access them anytime.
            </p>
            <Link to="/signin">
              <Button>Sign In</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const availableItems = savedItems.filter(item => item.is_available);
  const unavailableItems = savedItems.filter(item => !item.is_available);

  return (
    <Layout>
      <div className="min-h-screen bg-[var(--theme-bg)]">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] flex items-center gap-3">
                <Bookmark className="w-7 h-7 text-[#C9956C]" />
                Saved Items
              </h1>
              <p className="text-[var(--theme-text-secondary)] mt-1">
                {savedItems.length} item{savedItems.length !== 1 ? 's' : ''} saved
              </p>
            </div>

            <div className="flex items-center gap-4">
              <TileSizeSlider tileSize={tileSize} setTileSize={setTileSize} min={MIN_TILE_SIZE} max={MAX_TILE_SIZE} />
              <Link to="/catalog">
                <Button variant="outline">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Browse Catalog
                </Button>
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#C9956C]" />
            </div>
          ) : savedItems.length === 0 ? (
            <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-12 text-center">
              <Heart className="w-16 h-16 mx-auto text-[var(--theme-text-muted)] mb-4" />
              <h2 className="text-xl font-semibold text-[var(--theme-text-primary)] mb-2">
                No saved items yet
              </h2>
              <p className="text-[var(--theme-text-secondary)] mb-6 max-w-md mx-auto">
                When you find something you like, tap the bookmark icon to save it here for later.
              </p>
              <Link to="/catalog">
                <Button>
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Start Browsing
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Available Items */}
              {availableItems.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-[var(--theme-text-primary)] mb-4">
                    Available ({availableItems.length})
                  </h2>
                  <div style={gridStyle}>
                    {availableItems.map(item => (
                      <div key={item.id} className="relative group">
                        <CatalogCard item={item} />
                        
                        {/* Saved timestamp & unsave button overlay */}
                        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => handleUnsave(item.id, e)}
                            className="p-1.5 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors"
                            title="Remove from saved"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        
                        {/* Saved time badge */}
                        {item.saved_at && (
                          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px]">
                            Saved {formatDistanceToNow(new Date(item.saved_at), { addSuffix: true })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Unavailable Items */}
              {unavailableItems.length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    No Longer Available ({unavailableItems.length})
                  </h2>
                  <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
                    These items have been sold or removed by the seller.
                  </p>
                  <div style={gridStyle}>
                    {unavailableItems.map(item => (
                      <div key={item.id} className="relative group opacity-60">
                        <CatalogCard item={item} />
                        
                        {/* Sold/Unavailable overlay */}
                        <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center">
                          <span className="px-3 py-1 bg-black/80 text-white text-sm font-medium rounded-full">
                            {item.status === 'sold' ? 'SOLD' : 'UNAVAILABLE'}
                          </span>
                        </div>
                        
                        {/* Remove button */}
                        <button
                          onClick={(e) => handleUnsave(item.id, e)}
                          className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove from saved"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
